import { Model } from '@/models/Model';
import { Address } from '@/models/Address';
import { Token } from '@atabix/core';
import { Order } from './Order';


export class User extends Model {
  protected $name: string = 'User';
  protected $endpoint: string = '/users';
  protected $fillable: string[] = [
    'email', 'name', 'first_name', 'middle_name', 'last_name', 'avatar', 'phone', 'email', 'language', 'role', 'profile', 'company', 'has_different_invoice_address', 'my_referral_code'];

  public id?: string;
  public name?: string;
  public company?: string;
  public role?: string;
  public first_name?: string;
  public middle_name?: string;
  public last_name?: string;
  public display_name?: string;
  public avatar?: string;
  public phone?: string;
  public email?: string;
  public user_type?: string;
  public token?: Token;
  public address?: Address;
  public website?: string;
  public profile?: Profile;
  public password?: string;
  public qr_image?: string;
  public has_mfa?: boolean;
  public is_active?: boolean;
  public has_different_invoice_address?: boolean;
  public hey_flow_source?: string;
  public my_referral_code?: string;
  public referral_credit_logs?: ReferralCreditLog[];
  public note?: string;
  public language?: string;

  /**
   * Whether this colleague is on the Maia pilot.
   *
   * Computed by the backend (App\Services\Maia\MaiaAccess) rather than worked
   * out from `roles` here, because the same rule decides whether `maia:chat`
   * answers at all and it includes two named user ids the frontend never sees —
   * `id` on this model is the uuid. Absent means no: a stale user payload must
   * not open the widget.
   */
  public can_use_maia?: boolean;

  /**
   * Whether this colleague may rewrite Maia's instructions.
   *
   * Narrower than `can_use_maia`: the two named pilot colleagues can ask Maia
   * anything without being able to change what she is. Computed server-side
   * for the same reason — see App\Services\Maia\MaiaAccess. Absent means no.
   */
  public can_edit_maia_instructions?: boolean;

  /**
   * can be root, administrator, stylist, contact, support, supplier
   *
   * @type {(Role[] | RoleEnum[])}
   * @memberof User
   */
  public roles?: Role[] | RoleEnum[];

  public current(): Promise<any> {
    return this.filter('current', true)
      .include(['roles'])
      .first();
  }

  public isAuthenticated() {
    if (!Token.get('access')) {
      return false;
    }

    this.token = Token.get('access');

    return Token.get('access').length > 0;
  }

  public logout() {
    this.token = undefined;
    Token.delete(['access', 'refresh']);
  }

  public assignAsPlanner() {
    const url = `${this.$endpoint}/${this.resolvePrimaryKey()}/assign-as-planner`;
    return this.request("patch", url, {});
  }

  public removeAsPlanner() {
    const url = `${this.$endpoint}/${this.resolvePrimaryKey()}/remove-as-planner`;
    return this.request("patch", url, {});
  }

  public toggleIsActive(): Promise<any> {
    const url = `${this.$endpoint}/${this.resolvePrimaryKey()}/toggle-is-active`;
    return this.request("patch", url, {});
  }

  /**
   * compares role / roles to the stores user roles;
   * if any overlap, returns true.
   * if user is root / admin, returns true.
   *
   * @param {(RoleEnum | RoleEnum[])} rolesToCheck
   * @param checkAdmin
   * @returns boolean
   * @memberof User
   */

  public hasRole(rolesToCheck: RoleEnum | RoleEnum[], checkAdmin: boolean = true): boolean {
    if (typeof this.roles === 'undefined') {
      console.error('no roles set to check for')
      return false;
    }

    // params can be array of roles or role
    // if role (string) convert to array with 1
    if (!Array.isArray(rolesToCheck)) {
      rolesToCheck = [rolesToCheck];
    }

    const userRoleIds = (this.roles as Role[]).map(role => role.id);

    // Wtf is this below? Can't really change it now
    //is the user root / admin? return true.
    if (checkAdmin) {
      if (userRoleIds.includes(RoleEnum.ROOT) || userRoleIds.includes(RoleEnum.ADMINISTRATOR)) {
        return true;
      }
    }

    return userRoleIds
      .map(userRoleId => { // for every userRode, see if in param rolesToCheck
        return rolesToCheck.includes(userRoleId)
      })
      .includes(true); // if any match, array contains true.
  }

  /**
   * Check if a user has planner rights,
   * We do not use the above method, because a planner should see specific things extra,
   * Which doesn't mean an Admin needs to see them.
   */
  public isPlanner() {
    const userRoleIds = (this.roles as Role[]).map(role => role.id);

    return userRoleIds
      .map(userRoleId => { // for every userRode, see if in param rolesToCheck
        return [RoleEnum.PLANNER].includes(userRoleId)
      })
      .includes(true); // if any match, array contains true.
  }

  public canEditOrders() {
    return this.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT]);
  }

  public canEditUsers() {
    return this.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT]);
  }

  public canViewUsers() {
    return this.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT, RoleEnum.STYLIST, RoleEnum.PLANNER]);
  }

  public canViewLeads() {
    return this.hasRole([RoleEnum.ADMINISTRATOR, RoleEnum.ROOT, RoleEnum.SUPPORT]);
  }

  public canViewInvoices() {
    return this.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT, RoleEnum.PLANNER]);
  }

  public canViewKeptItems() {
    return this.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT, RoleEnum.PLANNER]);
  }

  public canViewAttachments() {
    return this.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT, RoleEnum.STYLIST, RoleEnum.PLANNER]);
  }

  public canViewLabels() {
    return this.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT, RoleEnum.STYLIST, RoleEnum.PLANNER]);
  }

  public canViewLabelAddress() {
    return this.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT, RoleEnum.PLANNER]);
  }

  public canEditPersonalNotes() {
    return this.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT, RoleEnum.STYLIST, RoleEnum.PLANNER]);
  }

  public canEditStylistNotes() {
    return this.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT, RoleEnum.STYLIST]);
  }

  public canViewUserOrOrderDetails() {
    return this.hasRole([RoleEnum.ROOT, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT, RoleEnum.PLANNER]);
  }
}

export enum RoleEnum {
  ROOT = 'root',
  ADMINISTRATOR = 'administrator',
  SUPPORT = 'support',
  STYLIST = 'stylist',
  PLANNER = 'planner',
  PHOTO_STUDIO = 'photo_studio',
  WAREHOUSE = 'warehouse',
}

interface Role {
  id: RoleEnum;
  name: string;
}

export interface Profile {
  user_id?: string,
  age?: string,
  street?: string;
  address_number?: string;
  address_addition?: string;
  address_additional_info?: string;
  zip_code?: string;
  city?: string;
  country?: string;
  company?: string;
  phone?: string;
  answers?: string;
  birthday_at?: string;
  repeat_intention_date?: string;
  repeat_next_order?: string;
  repeat_frequency?: string;
  invoice_address?: InvoiceAddress;
  zoho_contact_id?: string;
  has_different_invoice_address?: boolean;
  supplier_id?: boolean;
  status?: string;
  has_email_newsletter?: boolean;
  has_email_repeat_activation?: boolean;
  has_sms_repeat_activation?: boolean;
  has_email_alerts?: boolean;
  has_sms_alerts?: boolean;
  address_checked?: boolean;
  address_score?: number;
}

export interface InvoiceAddress {
  company?: string;
  address_addition?: string;
  address_additional_info?: string;
  address_number: string;
  city: string;
  street: string;
  zip_code: string;
  country: string;
}

export interface ReferralCreditLog {
  id?: string;
  referral_code_order?: Order;
}
